import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    ViewChild
} from '@angular/core';
import {
    MatCell, MatCellDef,
    MatColumnDef,
    MatHeaderCell, MatHeaderCellDef,
    MatHeaderRow, MatHeaderRowDef,
    MatRow, MatRowDef,
    MatTable,
    MatTableDataSource
} from '@angular/material/table';
import {Opportunity} from '../../interfaces/opportunity.interface';
import {debounceTime, distinctUntilChanged, filter, startWith, switchMap, take, takeUntil} from 'rxjs/operators';
import {Observable, Subject} from 'rxjs';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {ReactiveFormsModule, UntypedFormControl} from '@angular/forms';
import {OpportunityService} from '../../services/opportunity.service';
import {MAT_DIALOG_DATA, MatDialog} from '@angular/material/dialog';
import {OpportunityFormComponent} from '../opportunity-form/opportunity-form.component';
import {DataService} from '../../../services/data.service';
import {AppStateService} from '../../../services/app-state.service';
import {OpportunityStatuses} from '../../interfaces/opportunity-statuses.enum';
import {OpportunityStages} from '../../interfaces/opportunity-stages.enum';
import {AuthService} from '../../../services/auth.service';
import moment from 'moment/moment';
import {ConfigService} from '../../../services/config.service';
import {MatFormField, MatLabel, MatOption, MatSelect, MatSelectChange} from '@angular/material/select';
import {OpportunityContextualMenuComponent} from '../opportunity-contextual-menu/opportunity-contextual-menu.component';
import {MatIcon} from '@angular/material/icon';
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from '@angular/material/card';
import {MatMenu, MatMenuTrigger} from '@angular/material/menu';
import {MatProgressBar} from '@angular/material/progress-bar';
import {MatInput} from '@angular/material/input';
import {AsyncPipe, CurrencyPipe, DatePipe, KeyValuePipe, NgClass, NgStyle} from '@angular/common';
import {MatButton, MatIconButton} from '@angular/material/button';
import {TimestampPipe} from '../../../shared/pipes/timestamp.pipe';
import {MatTooltip} from '@angular/material/tooltip';



@Component({
    selector: 'app-opportunities-list',
    templateUrl: './opportunities-list-component.html',
    imports: [
        MatFormField,
        MatHeaderCell,
        MatCell,
        MatHeaderCell,
        MatCell,
        MatCell,
        MatCell,
        MatTable,
        MatRow,
        MatHeaderRow,
        MatPaginator,
        OpportunityContextualMenuComponent,
        MatOption,
        MatSelect,
        MatLabel,
        MatIcon,
        MatCardTitle,
        MatMenu,
        MatProgressBar,
        MatCardContent,
        MatCard,
        ReactiveFormsModule,
        MatInput,
        CurrencyPipe,
        AsyncPipe,
        MatMenuTrigger,
        MatIconButton,
        MatColumnDef,
        KeyValuePipe,
        NgClass,
        TimestampPipe,
        DatePipe,
        NgStyle,
        MatTooltip,
        RouterLink,
        MatButton,
        MatCellDef,
        MatCellDef,
        MatCellDef,
        MatHeaderRowDef,
        MatRowDef,
        MatSort,
        MatSortHeader,
        MatHeaderCellDef,
        MatHeaderCellDef,
        MatCardHeader
    ],
    styleUrls: [
        './opportunity-list.component.scss'
    ]
})
export class OpportunitiesListComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;

    @ViewChild(MatSort, {static: true}) sort: MatSort;

    public datasource: MatTableDataSource<Opportunity> = new MatTableDataSource<Opportunity>([]);

    private unsubscribe$: Subject<void> = new Subject<void>();

    @Output() opportunitySelection: EventEmitter<Opportunity> = new EventEmitter<Opportunity>();
    @Output() latestOpportunity: EventEmitter<Opportunity> = new EventEmitter<Opportunity>();

    displayedColumns = [
        'status',
        'opportunityStage',
        'salesManager',
        'budgetNr',
        'companyName',
        'opportunityName',
        'nextSteps',
        'potentialClosure',
        'proposalSentOn',
        'proposalRequested',
        'followUpDate',
        'nextFollowUpDate',
        'forecastAmount',
        'actions'
    ];

    isLoading = true;

    budgetNrFilterControl = new UntypedFormControl(null);
    companyNameFilterControl = new UntypedFormControl(null);
    opportunityNameFilterControl = new UntypedFormControl(null);
    salesManagerFilterControl = new UntypedFormControl(null);

    refreshList$: Subject<void> = new Subject<void>();

    readonly OpportunityStatuses = OpportunityStatuses;
    readonly OpportunityStages = OpportunityStages;

    appliedStageFilter: OpportunityStages[] = [];
    appliedStatusFilter: OpportunityStatuses[] = [];

    storedStageSelection: string[] = [];
    storedStatusSelection: string[] = [];

    preferredPageSize: number;

    storedFilter: string | null = null;

    totalAmountITA: number;
    totalAmountUSA: number;

    private _hasAddOpportunityRole = false;
    public pendingAction$: Observable<boolean>;

    get hasAddOpportunityRole() {
        return this._hasAddOpportunityRole;
    }

    public linkDashboardOpportunities: string;

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private opportunityService: OpportunityService,
        private dataService: DataService,
        public appState: AppStateService,
        @Optional() @Inject(MAT_DIALOG_DATA) protected list: Observable<Opportunity[]>,
        private matDialog: MatDialog,
        private authService: AuthService,
        private configService: ConfigService
    ) {
        this.storedFilter = this.opportunityService.retrieveFilterFromLocalStorage();
        if (this.storedFilter) {
            // budgetNr + _#_ + salesManager + _#_ + companyName + _#_ + opportunityName + _#_ + opportunityStage + _#_ + opportunityStatus
            const split = this.storedFilter.split('_#_');
            this.budgetNrFilterControl.setValue(split[0] ?? null);
            this.salesManagerFilterControl.setValue(split[1] ?? null);
            this.companyNameFilterControl.setValue(split[2] ?? null);
            this.opportunityNameFilterControl.setValue(split[3] ?? null);
            this.storedStageSelection = split[4].split(',') || [];
            this.storedStatusSelection = split[5].split(',') || [];
        }

        this.preferredPageSize = this.opportunityService.retrievePageSizePreferences();
        this.pendingAction$ = this.opportunityService.pendingAction$;

        this.configService.settings
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(val => {
                if (val?.value) {
                    this.linkDashboardOpportunities = val.value.linkDashboardOpportunities;
                }
            });
    }

    ngOnInit() {
        this.budgetNrFilterControl.valueChanges.pipe(
            debounceTime(100),
            distinctUntilChanged()
        ).subscribe(() => this.updateAppliedFilter());

        this.salesManagerFilterControl.valueChanges.pipe(
            debounceTime(100),
            distinctUntilChanged()
        ).subscribe(() => this.updateAppliedFilter());

        this.opportunityNameFilterControl.valueChanges.pipe(
            debounceTime(100),
            distinctUntilChanged()
        ).subscribe(() => this.updateAppliedFilter());

        this.companyNameFilterControl.valueChanges.pipe(
            debounceTime(100),
            distinctUntilChanged()
        ).subscribe(() => this.updateAppliedFilter());

        this.activatedRoute.queryParamMap.subscribe(paramsMap => {
            if (paramsMap.has('budgetNr')) {
                this.budgetNrFilterControl.setValue(paramsMap.get('budgetNr'));
                this.budgetNrFilterControl.markAsTouched();
            }
        });

        this.authService.userHasPermissions(
            [
                'app.opportunity.create'
            ])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((val) => {
                this._hasAddOpportunityRole = val[0];
            });
    }

    ngAfterViewInit() {
        this.paginator.pageSize = this.preferredPageSize ?? 10;
        this.datasource.sortData = this.tableSort.bind(this);
        this.datasource.filterPredicate = this.filterPredicate.bind(this);
        this.datasource.paginator = this.paginator;
        this.datasource.sort = this.sort;

        this.paginator.page.pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(pageEvent => {
            this.opportunityService.storePageSizePreferences(pageEvent.pageSize);
        });

        this.refreshList$.pipe(
            startWith(true),
            takeUntil(this.unsubscribe$),
            switchMap(() => this.opportunityService.getOpportunitiesList$())
        ).subscribe((opportunities: Opportunity[]) => {
            this.datasource.data = opportunities;
            if (this.storedFilter) {
                this.datasource.filter = this.storedFilter;
            }
            this.paginator.length = opportunities.length;
            this.isLoading = false;
            this.updateAmounts();
        });

        this.sort.sortChange
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                this.paginator.pageIndex = 0;
            });
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    openOpportunityFormDialog() {
        this.matDialog.open(OpportunityFormComponent, {
            disableClose: true,
            width: '80vw',
            maxWidth: '1000px',
            maxHeight: '90vh'
        }).afterClosed().pipe(
            take(1),
            filter(result => !!result)
        ).subscribe((data: { opportunity: Opportunity, cosmoId: string | null, createBudget: boolean }) => {
            console.log('New opportunity dialog closed: ', data);
            const opp = {...data.opportunity, cosmoId: data.cosmoId};
            this.dataService.createNewOpportunity(opp, data.createBudget).then(result => {
                console.log('Opportunity created successfully');
            }).catch(error => {
                console.error('error creating new opportunity', error);
            });
        });
    }

    stageSelectionChange(ev: MatSelectChange) {
        this.appliedStageFilter = ev.value;
        this.updateAppliedFilter();
    }

    statusSelectionChange(ev: MatSelectChange) {
        this.appliedStatusFilter = ev.value;
        this.updateAppliedFilter();
    }

    isNextFollowUpDateExpired(opportunity: Opportunity) {
        const dateDiff = moment(opportunity.nextFollowUpDate).diff(moment(new Date()), 'days');
        return dateDiff < 0 ? 'red' : (dateDiff < 7) ? 'orange' : 'black';
    }

    private updateAppliedFilter() {
        const budgetNrFilter = this.budgetNrFilterControl.value?.trim() ?? '';
        const salesManagerFilter = this.salesManagerFilterControl.value?.trim() ?? '';
        const opportunityNameFilter = this.opportunityNameFilterControl.value?.trim() ?? '';
        const companyNameFilter = this.companyNameFilterControl.value?.trim() ?? '';
        const filterString = budgetNrFilter + '_#_' + salesManagerFilter + '_#_' + companyNameFilter + '_#_' +
            opportunityNameFilter + '_#_' + this.appliedStageFilter.join(',') + '_#_' + this.appliedStatusFilter.join(',');
        this.datasource.filter = filterString;

        this.opportunityService.storeFilterInLocalStorage(filterString === '_#__#__#__#__#_' ? null : this.datasource.filter);
        this.updateAmounts();
    }

    private updateAmounts() {
        this.totalAmountITA = this.datasource.filteredData.filter(o => o.currency === 'EUR')
            .reduce((sum, current) => sum + current.forecastAmountITA, 0);

        this.totalAmountUSA = this.datasource.filteredData.filter(o => o.currency === 'USD')
            .reduce((sum, current) => sum + current.forecastAmountITA, 0);
    }

    private filterPredicate(opportunity: Opportunity, filterS: string) {
        // budgetNr + _#_ + salesManager + _#_ + companyName + _#_ + opportunityName + _#_ + opportunityStage + _#_ + opportunityStatus

        const split = filterS.split('_#_');
        const budgetNrCondition = split[0] ? opportunity.budgetNr?.toLowerCase().includes(split[0].toLowerCase()) : true;
        const salesManagerCondition = split[1] ? opportunity.salesManager?.toLowerCase().includes(split[1].toLowerCase()) : true;
        const opportunityNameCondition = split[3] ? opportunity.opportunityName?.toLowerCase().includes(split[3].toLowerCase()) : true;
        const companyNameCondition = split[2] ? opportunity.companyName?.toLowerCase().includes(split[2].toLowerCase()) : true;
        const stageCondition = split[4] ? split[4].split(',').includes(opportunity.opportunityStage) : true;
        const statusCondition = split[5] ? split[5].split(',').includes(opportunity.status) : true;

        return budgetNrCondition && salesManagerCondition && opportunityNameCondition &&
            companyNameCondition && stageCondition && statusCondition;
    }

    private tableSort(items: Opportunity[], sort: MatSort): Opportunity[] {
        const sortDirection = (sort.direction === 'asc') ? 1 : -1;
        switch (sort.active) {
            case 'budgetNr':
                return items.sort((a, b) => a.budgetNr?.localeCompare(b.budgetNr) * sortDirection);
            case 'companyName':
                return items.sort((a, b) => {
                    const companyA = a.companyName ? a.companyName : '';
                    const companyB = b.companyName ? b.companyName : '';
                    return companyA.localeCompare(companyB) * sortDirection;
                });
            case 'proposalSentOn':
                return items.sort((a, b) => {
                    if (!a.proposalSentOn && !b.proposalSentOn) return 0;
                    if (!a.proposalSentOn) return -sortDirection;
                    if (!b.proposalSentOn) return sortDirection;
                    return (a.proposalSentOn?.getTime() - b.proposalSentOn?.getTime()) * sortDirection;
                });
            case 'proposalRequested':
                return items.sort((a, b) => {
                    if (!a.proposalRequested && !b.proposalRequested) return 0;
                    if (!a.proposalRequested) return -sortDirection;
                    if (!b.proposalRequested) return sortDirection;
                    return (a.proposalRequested?.getTime() - b.proposalRequested?.getTime()) * sortDirection;
                });
            case 'followUpDate':
                return items.sort((a, b) => {
                    if (!a.followUpDate && !b.followUpDate) return 0;
                    if (!a.followUpDate) return -sortDirection;
                    if (!b.followUpDate) return sortDirection;
                    return (a.followUpDate?.getTime() - b.followUpDate?.getTime()) * sortDirection;
                });
            case 'nextFollowUpDate':
                return items.sort((a, b) => {
                    if (!a.nextFollowUpDate && !b.nextFollowUpDate) return 0;
                    if (!a.nextFollowUpDate) return -sortDirection;
                    if (!b.nextFollowUpDate) return sortDirection;
                    return (a.nextFollowUpDate?.getTime() - b.nextFollowUpDate?.getTime()) * sortDirection;
                });
            case 'forecastAmount':
                return items.sort((a, b) => {
                    return a.forecastAmountITA > b.forecastAmountITA ? sortDirection : -sortDirection;
                });
            default:
                return items;
        }
    }

    navigateToBudget(opportunity: Opportunity) {
        this.router.navigate(['budget', opportunity.budgetId]);
    }
}
